import type { components } from '@/types/api/v1'
import { GraviButton, Horizontal } from '@gravitate-js/excalibrr'
import { Form, type FormInstance, Input, InputNumber, Select, Popconfirm } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import { useMemo } from 'react'
import { ShiftTileList } from './ShiftTiles/ShiftTileList'
import { TemplateHeader } from './TemplateHeader'
import { daysOfTheWeek } from './utils'
import './templateManagement.css'

export type TemplateInput = components['schemas']['DriverScheduleTemplateView-Input'] & {
  length?: number
}

type TemplateFormProps = {
  initial?: TemplateInput
  onSubmit?: (val: TemplateInput) => void
  onClose?: () => void
  header?: string
}

const DAYS_IN_WEEK = 7
export const startDayOptions: DefaultOptionType[] = daysOfTheWeek.map((label, value) => ({ label, value }))
function StartDay({ form }: { form: FormInstance<TemplateInput> }) {
  const length = Form.useWatch('length', form)
  if (length !== DAYS_IN_WEEK) return null

  return (
    <Form.Item name='offset_day' label='Default Day of Week' style={{ width: '200px' }}>
      <Select placeholder='First day (optional)' allowClear style={{ width: '100%' }} options={startDayOptions} />
    </Form.Item>
  )
}

function DayInputs({
  value = [],
  onChange,
  form,
}: {
  value?: TemplateInput['days']
  onChange?: (val: TemplateInput['days']) => void
  form: FormInstance<TemplateInput>
}) {
  const length = Form.useWatch<number | undefined>(['length'], form)
  const offset_day = Form.useWatch<number | null | undefined>(['offset_day'], form)
  const filteredDays = useMemo(() => value.slice(0, length ?? 0), [value, length])

  return (
    <ShiftTileList
      offsetDay={offset_day ?? undefined}
      days={filteredDays}
      onChange={({ value: item, index }) => onChange?.(value.map((x, idx) => (idx === index ? item : x)))}
    />
  )
}

const MIN_DAYS = 2
const MAX_DAYS = 21
const formatDaysToForm = (days: TemplateInput['days'] = []) => days.concat(Array(MAX_DAYS - days.length).fill({}))

export function TemplateForm({ initial, header = 'Create New Template', onClose, onSubmit }: TemplateFormProps) {
  const [form] = Form.useForm<TemplateInput>()
  const initialValues = useMemo(
    () => ({ ...initial, days: formatDaysToForm(initial?.days), length: initial?.days.length }),
    [initial]
  )
  return (
    <Form
      onFinish={(x) => onSubmit?.({ ...x, days: x.days.slice(0, x.length) })}
      form={form}
      clearOnDestroy
      layout='vertical'
      className='template-container'
      initialValues={initialValues}
    >
      <TemplateHeader
        title={header}
        actions={
          <>
            <GraviButton buttonText='Cancel' onClick={onClose} />
            <Popconfirm
              onConfirm={form.submit}
              title='Save changes?'
              description="Edits regenerate schedules starting next Sunday. Drivers with assigned orders won't be updated."
              placement='bottomRight'
              okText='Yes'
              cancelText='No'
            >
              <GraviButton buttonText='Save Template' theme2 />
            </Popconfirm>
          </>
        }
      >
        <Horizontal gap={16} alignItems='flex-start'>
          <Form.Item
            name='name'
            label='Template Name'
            rules={[{ required: true, message: 'Please enter a template name' }]}
            style={{ flex: 2 }}
          >
            <Input placeholder='Enter template name' maxLength={50} showCount />
          </Form.Item>

          <Form.Item name='description' label='Description' style={{ flex: 2 }}>
            <Input placeholder='Enter description' maxLength={100} showCount />
          </Form.Item>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>

          <StartDay form={form} />

          <Form.Item
            name='length'
            label='Number of Days'
            rules={[
              { required: true, message: 'Please enter number of days' },
              { type: 'number', min: MIN_DAYS, max: MAX_DAYS, message: 'Days must be between 3 and 21' },
            ]}
            style={{ width: '200px' }}
          >
            <InputNumber
              disabled={!!initial?.id}
              min={MIN_DAYS}
              max={MAX_DAYS}
              style={{ width: '100%' }}
              controls={true}
              precision={0}
              keyboard={true}
            />
          </Form.Item>
        </Horizontal>
      </TemplateHeader>
      <Form.Item name='days' dependencies={['length, offset_days']}>
        <DayInputs form={form} />
      </Form.Item>
    </Form>
  )
}
